/**
 * Usage-history dialog state for a warehouse contact — tracks which
 * contact's history dialog is open and caches each contact's fetched
 * usage data (RMAs and order sales) so re-opening the dialog for the
 * same contact doesn't re-fetch.
 *
 * Shared by the contacts grid (desktop) and card list (mobile) views,
 * which otherwise each re-implemented the same 4-state dialog machine.
 *
 * Contains:
 * - useContactUsageHistory — dialog open/close + fetch-and-cache hook
 */
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { fetchUsageHistoryAPI } from '@sophocles/api/contacts';
import { addToast } from '@sophocles/features/toast';

const EMPTY_CACHE = {};

/**
 * @returns {{
 *   isOpen: boolean,
 *   contact: Object|null,
 *   usageData: Object|undefined,
 *   loading: boolean,
 *   open: (contact: Object) => Promise<void>,
 *   close: () => void,
 * }}
 */
export default function useContactUsageHistory() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [contact, setContact] = useState(null);
  const [cache, setCache] = useState(EMPTY_CACHE);
  const [loading, setLoading] = useState(false);

  const open = useCallback(
    async targetContact => {
      setContact(targetContact);
      setIsOpen(true);

      if (cache[targetContact.id]) {
        return;
      }

      setLoading(true);
      try {
        const usageData = await fetchUsageHistoryAPI(targetContact.id);
        setCache(prev => ({ ...prev, [targetContact.id]: usageData }));
      } catch (error) {
        console.error('Failed to load usage history:', error);
        dispatch(
          addToast({ message: t('contacts.usageHistory.error', 'Failed to load usage history'), level: 'error' }),
        );
        setCache(prev => ({ ...prev, [targetContact.id]: { error: true } }));
      } finally {
        setLoading(false);
      }
    },
    [cache, dispatch, t],
  );

  const close = useCallback(() => {
    setIsOpen(false);
    setContact(null);
  }, []);

  const usageData = contact ? cache[contact.id] : undefined;

  return useMemo(
    () => ({ isOpen, contact, usageData, loading, open, close }),
    [isOpen, contact, usageData, loading, open, close],
  );
}
